//
// Functional tools for composing pure function React components.
//

// the no-unused-prop-types rule causes eslint parser erors here
/* eslint
   react/no-unused-prop-types: 0,
   */

import * as React from 'react';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-recompose'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/react-recompose/dist/Recompose.cjs.js' implicitly has an 'any' type.
import {wrapDisplayName} from 'react-recompose';
import {
  defaults,
  assignWith,
  clone,
  isEmpty,
  isEqual as lodashIsEqual,
} from 'lodash';
import {constantFalse} from '@packages/utilities/fp/utils';
import {once} from '@packages/utilities/memo';

import {hasRenderableChildren} from '@packages/utilities/props';
// TODO: bring it back when theme wil no longer contain components
// import {type Theme} from '@packages/utilities/styled';

const isFunctionComponent = (value: unknown) =>
  typeof value === 'function' &&
  (!value.prototype || !value.prototype.setState);

/*
 * Creates a function that calls passed in functions
 * with the context and arguments received.
 */
export const chainFunctions = (
  ...fns: Array<any>
): ((...args: Array<unknown>) => void) => {
  return function (...args: Array<unknown>) {
    // @ts-expect-error - TS2683 - 'this' implicitly has type 'any' because it does not have a type annotation.
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    fns.forEach((fn) => fn.apply(self, args));
  };
};

type WithWrapper<P> = (arg1: React.ComponentType<P>) => React.FC<P>;

/**
 * Provides an outer wrapper component. For example:
 *
 * compose(
 *  withWrapper(({children}) => <div>{children}</div>)
 * )(props => <span />)
 *
 * would yield <div><span /></div>
 */

export const withWrapper =
  <P extends Record<any, any>>(
    render: (arg1: P) => React.ReactElement
  ): WithWrapper<P> =>
  (BaseComponent) =>
  (props: P) =>
    render({...props, children: <BaseComponent {...props} />});

/*
 * Wraps a stateless function component with in a class component with
 * the `shouldComponentUpdate` method defined. The predicate gets called
 * on each potential re-render of an element with the current and next props
 * as arguments. The re-render is bypassed if the predicate returns false.
 *
 * Optionally, a name may be passed and will be attached to the resulting
 * component to make debugging easier.
 */
export const shouldUpdate =
  <P extends Record<any, any>>(
    predicate: (arg1: P, arg2: P) => boolean
  ): ((
    Component: (arg1: P, arg2: unknown) => React.ReactElement,
    name?: string
  ) => React.ComponentType<P>) =>
  (
    Component: (arg1: P, arg2: unknown) => React.ReactElement,
    name?: string
  ): React.ComponentType<P> => {
    const ShouldUpdate = class extends React.Component<P> {
      static displayName = name || wrapDisplayName(Component, 'shouldUpdate');

      shouldComponentUpdate(nextProps: P) {
        return predicate(this.props, nextProps);
      }

      render() {
        return isFunctionComponent(Component) ? (
          Component(this.props, this.context)
        ) : (
          // @ts-expect-error [PDTY-2706] - TS2698 - Spread types may only be created from object types.
          <Component {...this.props} {...this.context} />
        );
      }
    };

    // @ts-expect-error - TS2339 - Property 'contextTypes' does not exist on type '(arg1: P, arg2: unknown) => ReactElement<any, string | JSXElementConstructor<any>>'.
    if (Component.contextTypes) {
      // @ts-expect-error - TS2551 - Property 'contextTypes' does not exist on type 'typeof ShouldUpdate'. Did you mean 'contextType'? | TS2339 - Property 'contextTypes' does not exist on type '(arg1: P, arg2: unknown) => ReactElement<any, string | JSXElementConstructor<any>>'.
      ShouldUpdate.contextTypes = Component.contextTypes;
    }

    return ShouldUpdate;
  };

/*
 * Returns a version of the passed in component that never re-renders.
 *
 * (Component, String) -> Component
 */
type Unchanging = <P>(arg1: P) => P;
export const unchanging: Unchanging = shouldUpdate(constantFalse) as any;

// TODO: This can be come quite descriptive, like
// type styleObjectType = {position?: 'absolute' | 'relative' | 'fixed'};
// To prevent typos and assinging unexisted css values

/**
 * Returns a component that only renders if the predicate returns
 * a truthy value given the props.
 *
 * Function -> Component -> Component
 */
const renderCondition =
  <P extends Record<any, any>>(predicate: (arg1: P) => boolean) =>
  (Component: React.ComponentType<P> | string) => {
    return React.forwardRef<any, P>((props, ref) =>
      predicate(props)
        ? React.createElement(Component, {...props, ref} as any)
        : null
    );
  };

/**
 * This pattern returns a component that only renders if its "when" predicate is true,
 * which is based on a prop condition, e.g. if prop.type === 'STYLE_OPACITY',
 * render the Opacity component, otherwise render the default component.
 *
 * Returns a HOC to apply on a default component.
 */
export const branched =
  <P extends Record<any, any>>(
    states: Array<{
      when: (arg1: P) => boolean;
      render: React.ComponentType<P>;
    }>
  ): ((
    DefaultComponent: React.ComponentType<P>
  ) => (props: P) => React.ReactElement) =>
  (DefaultComponent: React.ComponentType<P>) =>
  (props: P) => {
    const componentState = states.find(({when}) => when(props));
    const RenderedComponent =
      componentState == null ? DefaultComponent : componentState.render;

    return React.createElement(RenderedComponent, props);
  };

/**
 * Returns a component that only renders the passed in component if
 * there are any renderable children.
 *
 * Component -> Component
 */
export const noEmpty: <C extends React.ElementType>(arg1: C) => C =
  // @ts-expect-error [PDTY-2706] - TS2345 - Argument of type '({ children }: React.ReactNode) => boolean' is not assignable to parameter of type '(arg1: Record<any, any>) => boolean'.
  renderCondition(hasRenderableChildren) as any;

const buildListener = (oldFn: any, newFn: any) =>
  oldFn ? chainFunctions(newFn, oldFn) : newFn;

/**
 * Makes it possible to attach multiple listeners of the same type
 * on an element
 * @param  {Object} listeners Map of listeners to attach
 * @param  {Object} element   React element
 * @return {Object}           Cloned React element
 **/
export const chainListeners = <P extends Record<any, any>>(
  listeners: Partial<P>,
  element: {
    props: P;
  }
): any =>
  defaults(
    {
      props: (assignWith as any)(
        clone(element.props),
        listeners,
        buildListener
      ),
    },
    element
  );

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const buildProp = <A extends unknown, B extends unknown>(
  oldProp: A,
  newProp: B
): A | B => {
  if (!oldProp) {
    return newProp;
  }

  if (typeof oldProp === 'function' && typeof newProp === 'function') {
    // @ts-expect-error - TS2322 - Type '(...args: unknown[]) => void' is not assignable to type 'A | B'.
    return chainFunctions(newProp, oldProp);
  }

  return oldProp;
};

export const safeMergeProps = <P extends Record<any, any>>(
  propsA: P,
  propsB: Partial<P>
): P => assignWith(clone(propsA), propsB, buildProp);

/**
 * Clones a React element, merging new props into it via `safeMergeProps`
 * (i.e. preserving the child's own handlers/props where appropriate rather
 * than clobbering them).
 *
 * Optionally accepts a `ref` that will be attached to the cloned element.
 * Callers forwarding a ref alongside the child's own ref should merge them
 * with `useMergeRefs` (or equivalent) in the component body first — this
 * helper accepts the already-merged ref and does not perform merging itself,
 * since ref merging requires hook memoization to avoid detach/reattach
 * churn across renders.
 */
export const safeCloneElement = <P extends Record<any, any>>(
  props: P,
  element: React.ReactElement<React.ComponentProps<React.ComponentType<P>>>,
  ref?: React.Ref<unknown>
): React.ReactElement<React.ComponentProps<React.ComponentType<P>>> =>
  defaults(
    {
      ...(ref !== undefined && {ref}),
      props: safeMergeProps(element.props, props),
    },
    element
  );

/*
 * Used with recompose's withState and will grab an initial value set on the component's props
 * or provide a reasonable default for the state prop (allows overriding state)
 * example:
 *
 * const state = withState('varName', 'setVarName', getDefaultState('varName', true));
 *
 * So, varName above can either be a prop on the component or provided via withState
 */
export const getDefaultState =
  (key: string, defaultValue: any): ((props?: any) => any) =>
  (props: Record<any, any> = {}) =>
    props[key] || defaultValue;

/*
 * withDefaultState is _heavily_ inspired by recompose's withState higher order component.
 * See: https://github.com/acdlite/recompose/blob/master/src/packages/recompose/withState.js
 *
 * The original withState can accept state similar to props, but you cannot override them unfortunately.
 * This HOC accepts state, just like withState, but also watches incoming props and synchronizes it to the component's state so a parent component
 * can take "control" of the component.
 *
 * The idea is this: Define default behavior/state using withDefaultState, but allow parent components
 * to override it via props
 *
 * @param (String)  stateName  Name of property to include as "state"
 * @param (String)  stateUpdaterName   Name of generated function to update the piece of "state"
 * @param (Object | (props: Object) => any)  Default value or function to derive state from initial props
 * @return (Component)
 */
export const withDefaultState =
  (
    stateName: string,
    stateUpdaterName: string,
    initialState: any | any
  ): ((
    stateName: string,

    stateUpdaterName: string,

    initialState?: any
  ) => (BaseComponent?: any) => any) =>
  // @ts-expect-error - TS2322 - Type '(BaseComponent: any) => typeof (Anonymous class)' is not assignable to type '(stateName: string, stateUpdaterName: string, initialState?: any) => (BaseComponent?: any) => any'.
  (BaseComponent: any) => {
    const factory = (factoryProps: any) =>
      React.createElement(BaseComponent, factoryProps);
    return class extends React.Component {
      static displayName = wrapDisplayName(BaseComponent, 'withDefaultState');

      state = {
        stateValue:
          typeof initialState === 'function'
            ? initialState(this.props)
            : initialState,
      };

      UNSAFE_componentWillReceiveProps(nextProps: any) {
        Object.keys(nextProps).some((propKey) => {
          if (
            stateName === propKey &&
            !lodashIsEqual(nextProps[propKey], this.state.stateValue)
          ) {
            this.setState({stateValue: nextProps[propKey]});
            return true;
          }
          return false;
        });
      }

      updateStateValue = (updateFn: any, callback: any) =>
        this.setState(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          ({stateValue}) => ({
            stateValue:
              typeof updateFn === 'function' ? updateFn(stateValue) : updateFn,
          }),
          callback
        );

      render() {
        return factory({
          ...this.props,

          [stateName]: this.state.stateValue,
          [stateUpdaterName]: this.updateStateValue,
        });
      }
    };
  };

type StateValue = {
  updaterName: string;
  initialValue?: any | any;
};

type StateTransform = (
  props: Record<any, any>,
  state: Record<any, any>
) => Record<any, any>;
type StateSchema = {
  [stateValue: string]: StateValue;
};

/*
 * syncPropsToState is similar to withDefaultState, but accepts multiple root level props
 * via a StateSchema instead of a single value. THis enabled syncPropsToState to batch
 * state updates if more than one prop changes in UNSAFE_componentWillReceiveProps.
 *
 * Usage: Define default behavior/state using syncPropsToState, but allow parent components
 * to override it via props
 *
 * @param (StateSchema)  schema   Define the props, their setter name and initial value
 * @param (StateTransform?) transform   Take props and returns associated state AND transformed props
 * @return (Component)
 */
export const syncPropsToState: any =
  <Schema extends StateSchema>(
    schema: Schema,
    transform: StateTransform = () => ({})
  ) =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  <P extends unknown>(BaseComponent: React.ComponentType<P>) => {
    const schemaKeys = Object.keys(schema);
    const factory = (factoryProps: any) =>
      // @ts-expect-error - TS2769 - No overload matches this call.
      React.createElement(BaseComponent, factoryProps);

    return class extends React.Component<any> {
      static displayName = wrapDisplayName(BaseComponent, 'syncPropsToState');
      state: Record<any, any> = getInitialStateFromSchema(schema, this.props);

      static getDerivedStateFromProps(nextProps: any, prevState: any) {
        const transformedProps = transform(nextProps, prevState);
        const transformedKeys = Object.keys(transformedProps);

        const newState: Record<any, any> = {};
        // Synchronize props if not transformed, if in schema, and if unequal to state
        Object.keys(nextProps).forEach((propKey) => {
          if (
            !transformedKeys.includes(propKey) &&
            schemaKeys.includes(propKey) &&
            !lodashIsEqual(nextProps[propKey], prevState[propKey])
          ) {
            newState[propKey] = nextProps[propKey];
          }
        });
        // Merge any dependent state that may have been returned from transform
        const transformedState = {...newState, ...transformedProps} as const;

        if (!isEmpty(transformedState)) {
          return transformedState;
        }
        return null;
      }

      generateUpdateFunctions = once(() =>
        Object.keys(schema).reduce<Record<string, any>>(
          (functions: Record<any, any>, stateName: string) => {
            // @ts-expect-error - TS2339 - Property 'updaterName' does not exist on type 'StateValue | undefined'.
            const {updaterName} = schema[stateName];
            functions[updaterName] = (updateFn: any, callback: any) => {
              this.setState((prevState) => {
                return {
                  ...prevState,
                  [stateName]:
                    typeof updateFn === 'function'
                      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        updateFn(prevState[stateName])
                      : updateFn,
                };
              }, callback);
            };
            return functions;
          },
          {}
        )
      );

      render() {
        return factory({
          ...this.props,
          ...this.state,
          ...this.generateUpdateFunctions(),
        });
      }
    };
  };

export const getInitialStateFromSchema = (
  stateNameOrSchema: StateSchema,
  props: Record<any, any>
): Record<any, any> =>
  Object.keys(stateNameOrSchema).reduce<Record<string, any>>(
    (state: Record<any, any>, stateKey: string) => {
      const item = stateNameOrSchema[stateKey];
      // @ts-expect-error - TS2339 - Property 'initialValue' does not exist on type 'StateValue | undefined'.
      const {initialValue} = item;

      state[stateKey] =
        typeof initialValue === 'function' ? initialValue(props) : initialValue;

      return state;
    },
    {}
  );
